import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AddProfileImage } from '@components/add-profile-image';
import { Button } from '@components/button';
import { IconArrowRight } from '@components/icons/arrow-right';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';

import { useEditPreboarding } from 'api/preboarding/hooks';
import { useMyDetails } from 'api/users/hooks';
import { Title } from 'ui/unauthenticated/pages/common';

import {
  ButtonsContainer,
  ContentContainer,
  PreboardingStepper,
  PreboardingPageContainer,
} from './common';

import { routePaths } from '../navigation';

const ProfilePicForm = () => {
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();

  const nextPage = () => {
    navigate(routePaths.team);
  };

  const editMutation = useEditPreboarding({ onSuccess: nextPage });
  const detailsQuery = useMyDetails();

  const [picture, setPicture] = useState<File | null>();
  const [clearPicture, setClearPicture] = useState(false);

  if (detailsQuery.isLoading) {
    return <Spinner />;
  }

  const user = detailsQuery.data;

  const handleSave = () => {
    const formData = new FormData();
    if (picture) {
      formData.append('picture', picture);
    } else if (clearPicture) {
      formData.append('picture', '');
    }

    return editMutation.mutate(formData);
  };

  return (
    <ContentContainer>
      <Stack space={2} align="center">
        <Title>{t('welcome')}</Title>
        <Text font="regular16" color="textSecondary" text={t('description')} />
      </Stack>

      <ContentContainer>
        <Stack align="center">
          <AddProfileImage
            name={user?.picture && !clearPicture ? user.email : undefined}
            initalPicture={user?.picture}
            picture={picture}
            setPicture={setPicture}
            clearPicture={clearPicture}
            setClearPicture={setClearPicture}
            size="large"
          />
        </Stack>
        <ButtonsContainer>
          <Button
            color="textSecondary"
            text={t('skip')}
            iconRight={<IconArrowRight color="textSecondary" />}
            style={{ backgroundColor: theme.color.uiTrans5 }}
            onClick={nextPage}
          />
          <Button
            variant="primary-blue"
            text={t('continue')}
            onClick={handleSave}
            disabled={!Boolean(picture)}
          />
        </ButtonsContainer>
      </ContentContainer>
      <PreboardingStepper currentIndex={0} />
    </ContentContainer>
  );
};

export const SetProfilePicPage = () => {
  return (
    <PreboardingPageContainer>
      <ProfilePicForm />
    </PreboardingPageContainer>
  );
};

const locale = {
  en: {
    welcome: 'Welcome to Journeys',
    description: 'Please add a profile image to personalize your account',
    skip: 'Skip',
    continue: 'Continue',
  },
};
